export type ImagePaintDrawType = "gpt_image_2" | "nano_banana";
export type NanoBananaImageSize = "1K" | "2K" | "4K";

export const GPT_IMAGE_DISPLAY_NAME = "GPT-Image-2.5";
export const GPT_IMAGE_DEFAULT_QUALITY = "high";

export const GPT_IMAGE_2_SIZE_OPTIONS = [
  { aspectRatio: "1:1", size: "1024x1024" },
  { aspectRatio: "3:2", size: "1248x832" },
  { aspectRatio: "2:3", size: "832x1248" },
  { aspectRatio: "4:3", size: "1152x864" },
  { aspectRatio: "3:4", size: "864x1152" },
  { aspectRatio: "16:9", size: "1280x720" },
  { aspectRatio: "9:16", size: "720x1280" },
  { aspectRatio: "21:9", size: "1344x576" },
  { aspectRatio: "9:21", size: "576x1344" },
] as const;

export const GPT_IMAGE_2_CUSTOM_SIZE_LIMITS = {
  minDimension: 512,
  maxDimension: 2048,
  dimensionStep: 16,
  minPixels: 655_360,
  maxPixels: 2_097_152,
  maxAspectRatio: 3,
} as const;

export const getGptImageCustomSizeError = (
  width: number,
  height: number,
): string => {
  const {
    minDimension,
    maxDimension,
    dimensionStep,
    minPixels,
    maxPixels,
    maxAspectRatio,
  } = GPT_IMAGE_2_CUSTOM_SIZE_LIMITS;
  if (!Number.isInteger(width) || !Number.isInteger(height)) {
    return "请输入整数尺寸";
  }
  if (
    width < minDimension ||
    height < minDimension ||
    width > maxDimension ||
    height > maxDimension
  ) {
    return `宽高需在 ${minDimension}–${maxDimension} 之间`;
  }
  if (width % dimensionStep || height % dimensionStep) {
    return `宽高需为 ${dimensionStep} 的倍数`;
  }
  const pixels = width * height;
  if (pixels < minPixels || pixels > maxPixels) {
    return "总像素需在约 0.66–2.1 百万之间";
  }
  const ratio = Math.max(width / height, height / width);
  if (ratio > maxAspectRatio) {
    return `长短边比例不能超过 ${maxAspectRatio}:1`;
  }
  return "";
};

export const mergeGptImageSizeOptions = <
  T extends { dictKey: string; dictValue: string },
>(options: T[] = []): Array<T | { dictKey: string; dictValue: string }> => {
  const optionBySize = new Map(
    options.map((option) => [option.dictValue, option]),
  );
  return GPT_IMAGE_2_SIZE_OPTIONS.map(({ aspectRatio, size }) => ({
    ...optionBySize.get(size),
    dictKey: aspectRatio,
    dictValue: size,
  })) as Array<T | { dictKey: string; dictValue: string }>;
};

export const NANO_BANANA_IMAGE_SIZE_OPTIONS: Array<{
  value: NanoBananaImageSize;
  label: string;
  multiplier: number;
}> = [
  { value: "1K", label: "1K · 1倍", multiplier: 1 },
  { value: "2K", label: "2K · 2倍", multiplier: 2 },
  { value: "4K", label: "4K · 4倍", multiplier: 4 },
];

export const getNanoBananaPaintCost = (
  referenceCount: number,
  imageSize: NanoBananaImageSize,
) => {
  const baseCount = referenceCount >= 2 ? 2 : 1;
  const multiplier =
    NANO_BANANA_IMAGE_SIZE_OPTIONS.find(
      (item) => item.value === imageSize,
    )?.multiplier || 1;
  return baseCount * multiplier;
};

const greatestCommonDivisor = (left: number, right: number): number => {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1;
};

export const getImageAspectRatio = (size?: string): string => {
  const matched = String(size || "")
    .trim()
    .match(/^(\d+)[x×](\d+)$/i);
  if (!matched) return "";
  const width = Number(matched[1]);
  const height = Number(matched[2]);
  if (!width || !height) return "";
  const divisor = greatestCommonDivisor(width, height);
  return `${width / divisor}:${height / divisor}`;
};

export type ImagePaintConversationType =
  | "paintDalle"
  | "paintNanoBanana";

export interface ImagePaintModelMeta {
  conversationType: ImagePaintConversationType;
  drawType: ImagePaintDrawType;
  name: string;
  route: string;
  countKey: "dalle3Count" | "nanoBananaCount";
}

export const IMAGE_PAINT_MODELS: ImagePaintModelMeta[] = [
  {
    conversationType: "paintDalle",
    drawType: "gpt_image_2",
    name: GPT_IMAGE_DISPLAY_NAME,
    route: "/dalle",
    countKey: "dalle3Count",
  },
  {
    conversationType: "paintNanoBanana",
    drawType: "nano_banana",
    name: "Nano Banana 2",
    route: "/nano-banana",
    countKey: "nanoBananaCount",
  },
];

export const isImagePaintConversationType = (
  type?: string,
): type is ImagePaintConversationType =>
  IMAGE_PAINT_MODELS.some((item) => item.conversationType === type);

export const getImagePaintMetaByConversationType = (
  type?: string,
): ImagePaintModelMeta =>
  IMAGE_PAINT_MODELS.find((item) => item.conversationType === type) ||
  IMAGE_PAINT_MODELS[0];

export const getImagePaintMetaByDrawType = (
  drawType?: string,
): ImagePaintModelMeta =>
  IMAGE_PAINT_MODELS.find((item) => item.drawType === drawType) ||
  IMAGE_PAINT_MODELS[0];

export const getImagePaintMetaByPath = (pathname?: string): ImagePaintModelMeta =>
  IMAGE_PAINT_MODELS.find((item) => item.route === pathname) ||
  IMAGE_PAINT_MODELS[0];

export const isNanoBananaEnabled = (
  siteConfig?: Record<string, string>,
): boolean => {
  const value = String(
    siteConfig?.nano_banana_enabled ??
      siteConfig?.nanoBananaEnabled ??
      "",
  ).toLowerCase();
  return value === "true" || value === "1";
};

export const resolveImagePaintDrawType = (
  drawType?: string,
  featureCode?: string | null,
): ImagePaintDrawType | undefined => {
  if (drawType === "gpt_image_2" || drawType === "nano_banana") {
    return drawType;
  }

  const normalizedFeatureCode = String(featureCode || "").toLowerCase();
  if (normalizedFeatureCode === "nano_banana") {
    return "nano_banana";
  }
  if (
    ["dalle-3", "dalle3", "dalle_3", "gpt_image_2"].includes(
      normalizedFeatureCode,
    )
  ) {
    return "gpt_image_2";
  }

  return undefined;
};
